import React, { useState } from 'react';
import { saveKnowledgebase, saveSettings } from '../utils/firebaseSettings';

const OnboardingModal = ({ isOpen, onClose, userEmail, onSettingsUpdate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [signOffName, setSignOffName] = useState('');
  const [personality, setPersonality] = useState('professional');
  const [knowledgebaseFile, setKnowledgebaseFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen) return null;

  const steps = [
    {
      title: 'Welcome to Inbox Mate!',
      description: 'Transform your Gmail inbox into a visual Kanban board for better email management.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'Organize with Drag & Drop',
      description: 'Move emails between Open, In Progress, and Done columns. Your emails are automatically labeled in Gmail.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      ),
    },
    {
      title: 'AI-Powered Responses',
      description: 'Upgrade to Pro to generate smart email replies with AI. Customize with your own knowledge base and response personality.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: 'Customize AI Settings',
      type: 'form',
      description: 'Set up your AI preferences (optional - you can skip this step)',
    },
    {
      title: 'Ready to Get Started!',
      description: 'Your inbox emails are already loaded. Start organizing them into columns and take control of your support workflow.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const handleNext = async () => {
    // If on the AI settings form step, save settings before proceeding
    if (currentStep === 3) {
      setIsUploading(true);
      try {
        // Save knowledge base if file is selected
        if (knowledgebaseFile) {
          const reader = new FileReader();
          reader.onload = async (event) => {
            const content = event.target.result;
            await saveKnowledgebase(content, userEmail);
          };
          reader.readAsText(knowledgebaseFile);
        }

        // Save settings
        const settings = {
          signOffName,
          personality,
          kbLink: '',
        };
        await saveSettings(settings, userEmail);

        // Notify parent to update settings
        if (onSettingsUpdate) {
          onSettingsUpdate(settings, knowledgebaseFile ? await readFileAsText(knowledgebaseFile) : null);
        }
      } catch (error) {
        console.error('Error saving onboarding settings:', error);
      }
      setIsUploading(false);
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.readAsText(file);
    });
  };

  const handleSkip = () => {
    onClose();
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-8 bg-blue-600'
                      : index < currentStep
                      ? 'w-2 bg-blue-600'
                      : 'w-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-500">
              {currentStep + 1} / {steps.length}
            </span>
          </div>
        </div>

        {/* Content */}
        {currentStepData.type === 'form' ? (
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
              {currentStepData.title}
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
              {currentStepData.description}
            </p>

            <div className="space-y-6 max-w-lg mx-auto">
              {/* Sign-off Name */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Sign-off Name
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Your name to use when signing off AI-generated emails
                </p>
                <input
                  type="text"
                  value={signOffName}
                  onChange={(e) => setSignOffName(e.target.value)}
                  placeholder="e.g., Sarah, Support Team, etc."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Personality */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Response Personality
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Choose the tone and style for AI-generated responses
                </p>
                <select
                  value={personality}
                  onChange={(e) => setPersonality(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                >
                  <option value="professional">🎯 Professional - Clear, direct, and business-focused</option>
                  <option value="friendly">😊 Friendly - Warm, conversational, and approachable</option>
                  <option value="empathetic">💙 Empathetic - Compassionate, understanding, and supportive</option>
                  <option value="enthusiastic">✨ Enthusiastic - Energetic, positive, and encouraging</option>
                  <option value="concise">⚡ Concise - Brief, to-the-point, and efficient</option>
                </select>
              </div>

              {/* Knowledge Base Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Knowledge Base (Optional)
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Upload a file with information to help AI generate better responses
                </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    id="kb-upload-onboarding"
                    accept=".md,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setKnowledgebaseFile(file);
                      }
                    }}
                  />
                  <label
                    htmlFor="kb-upload-onboarding"
                    className="cursor-pointer inline-flex flex-col items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-xs font-medium text-gray-700">
                      {knowledgebaseFile ? knowledgebaseFile.name : 'Click to upload (.md or .txt)'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="flex justify-center mb-6">
              {currentStepData.icon}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {currentStepData.title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-lg mx-auto">
              {currentStepData.description}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Skip tutorial
          </button>
          <button
            onClick={handleNext}
            disabled={isUploading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              currentStep === steps.length - 1 ? 'Get Started' : 'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
